import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, unref as _unref, normalizeClass as _normalizeClass, vShow as _vShow } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "expiry-section"
}
const _hoisted_2 = { class: "list-heading" }
const _hoisted_3 = { class: "chart-container" }
const _hoisted_4 = { class: "chart-wrapper" }
const _hoisted_5 = {
  viewBox: "0 0 200 200",
  width: "200",
  height: "200"
}
const _hoisted_6 = ["stroke-dasharray"]
const _hoisted_7 = ["stroke-dasharray", "stroke-dashoffset"]
const _hoisted_8 = ["stroke-dasharray", "stroke-dashoffset"]
const _hoisted_9 = {
  x: "100",
  y: "95",
  "text-anchor": "middle",
  "font-size": "24",
  "font-weight": "bold",
  fill: "#333"
}
const _hoisted_10 = { class: "legend" }
const _hoisted_11 = ["onUpdate:modelValue"]
const _hoisted_12 = { class: "table-wrapper" }
const _hoisted_13 = { class: "table text-left table-hover bg-white text-dark" }
const _hoisted_14 = {
  key: 1,
  class: "pagination-container"
}
const _hoisted_15 = ["onClick"]

import { computed, ref, watch } from 'vue';
import { formatDate } from '@/Filters/dateFilter';

interface ExpiryRecord {
  documentExpiryResultId: number;
  officerName: string;
  contact: string;
  daysUntilExpiry: number;
  documentNumber: string;
  documentType: string;
  email: string;
  expiryDate: string;
  status: string;
}

interface LegendItem {
  key: string;
  label: string;
  color: string;
  count: number;
  checked: boolean;
}

interface Props {
  data: ExpiryRecord[];
  total: number;
  title: string;
  currentPage: number;
  startPage: number;
  endPage: number;
  isRangeGreater: boolean;
  isRangeLess: boolean;
  labelsPerPage: number;
  expiredCount?: number;
  immediatelyExpiringCount?: number;
  soonExpiringCount?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ExpiryTableSection',
  props: {
    data: {},
    total: {},
    title: {},
    currentPage: {},
    startPage: {},
    endPage: {},
    isRangeGreater: { type: Boolean },
    isRangeLess: { type: Boolean },
    labelsPerPage: {},
    expiredCount: {},
    immediatelyExpiringCount: {},
    soonExpiringCount: {}
  },
  emits: ["prev-page", "next-page", "page-change", "filter-change"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const circumference = 2 * Math.PI * 80;

// Tooltip state
const tooltipVisible = ref(false);
const tooltipText = ref('');
const tooltipX = ref(0);
const tooltipY = ref(0);

const legendItems = ref<LegendItem[]>([
  { key: 'expired', label: 'Expired', color: '#dc3545', count: 0, checked: true },
  { key: 'immediately', label: 'Expiring ≤ 7 days', color: '#ff6b6b', count: 0, checked: true },
  { key: 'soon', label: 'Expiring ≤ 30 days', color: '#ffc107', count: 0, checked: true }
]);

// Update counts when props change
watch(
  [() => props.expiredCount, () => props.immediatelyExpiringCount, () => props.soonExpiringCount],
  () => {
    legendItems.value[0].count = props.expiredCount ?? 0;
    legendItems.value[1].count = props.immediatelyExpiringCount ?? 0;
    legendItems.value[2].count = props.soonExpiringCount ?? 0;
  },
  { immediate: true }
);

const counts = computed(() => ({
  expired: props.expiredCount ?? 0,
  immediately: props.immediatelyExpiringCount ?? 0,
  soon: props.soonExpiringCount ?? 0
}));

const getTooltip = (label: string, count: number): string => {
  if (props.total === 0) return `${label}: 0 (0%)`;
  const percentage = ((count / props.total) * 100).toFixed(1);
  return `${label}: ${count} (${percentage}%)`;
};

const showTooltip = (event: MouseEvent, label: string, count: number) => {
  const percentage = props.total > 0 ? ((count / props.total) * 100).toFixed(1) : 0;
  tooltipText.value = `${label}: ${count} (${percentage}%)`;
  
  // Position tooltip near click/touch
  const rect = (event.target as SVGElement).getBoundingClientRect();
  tooltipX.value = event.clientX - 50;
  tooltipY.value = event.clientY - 40;
  
  tooltipVisible.value = true;
  
  // Auto hide after 3 seconds
  clearTimeout((window as any)._tooltipTimer);
  (window as any)._tooltipTimer = setTimeout(() => {
    tooltipVisible.value = false;
  }, 3000);
};

const onFilterChange = () => {
  emit('filter-change', legendItems.value);
};

const getDashArray = (count: number): string => {
  if (props.total === 0) return '0 502.65';
  const percentage = count / props.total;
  const dashLength = circumference * percentage;
  return `${dashLength} ${circumference - dashLength}`;
};

const getDashOffset = (offsetCount: number): number => {
  if (props.total === 0) return 0;
  const percentage = offsetCount / props.total;
  return -(circumference * percentage);
};

const getRowClass = (record: ExpiryRecord): string => {
  if (record.status === 'Expired') return 'row-expired';
  if (record.daysUntilExpiry <= 7 && record.daysUntilExpiry > 0) return 'row-immediately';
  if (record.daysUntilExpiry < 30 && record.daysUntilExpiry > 7) return 'row-soon';
  return '';
};

const isShownPageButton = (pageNo: number): boolean => {
  return (pageNo > props.startPage) && (pageNo <= (props.startPage + props.labelsPerPage));
};

__expose({ legendItems });

return (_ctx: any,_cache: any) => {
  return (__props.data.length >= 1)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, _toDisplayString(__props.title) + " as on " + _toDisplayString(new Date(__props.data[0]?.scanDate).toLocaleString('en-GB', { 
  day: '2-digit', 
  month: '2-digit', 
  year: 'numeric', 
  hour: '2-digit', 
  minute: '2-digit',
  hour12: false   // ensures 24-hour format
})), 1),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            (_openBlock(), _createElementBlock("svg", _hoisted_5, [
              _cache[5] || (_cache[5] = _createElementVNode("circle", {
                cx: "100",
                cy: "100",
                r: "80",
                fill: "transparent",
                stroke: "#e0e0e0",
                "stroke-width": "30"
              }, null, -1)),
              (counts.value.expired > 0)
                ? (_openBlock(), _createElementBlock("circle", {
                    key: 0,
                    cx: "100",
                    cy: "100",
                    r: "80",
                    fill: "transparent",
                    stroke: "#dc3545",
                    "stroke-width": "30",
                    "stroke-dasharray": getDashArray(counts.value.expired),
                    "stroke-dashoffset": 0,
                    transform: "rotate(-90 100 100)",
                    class: "pie-segment",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (showTooltip($event, 'Expired', counts.value.expired)))
                  }, [
                    _createElementVNode("title", null, _toDisplayString(getTooltip('Expired', counts.value.expired)), 1)
                  ], 8, _hoisted_6))
                : _createCommentVNode("", true),
              (counts.value.immediately > 0)
                ? (_openBlock(), _createElementBlock("circle", {
                    key: 1,
                    cx: "100",
                    cy: "100",
                    r: "80",
                    fill: "transparent",
                    stroke: "#ff6b6b",
                    "stroke-width": "30",
                    "stroke-dasharray": getDashArray(counts.value.immediately),
                    "stroke-dashoffset": getDashOffset(counts.value.expired),
                    transform: "rotate(-90 100 100)",
                    class: "pie-segment",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (showTooltip($event, 'Expiring ≤ 7 days', counts.value.immediately)))
                  }, [
                    _createElementVNode("title", null, _toDisplayString(getTooltip('Expiring ≤ 7 days', counts.value.immediately)), 1)
                  ], 8, _hoisted_7))
                : _createCommentVNode("", true),
              (counts.value.soon > 0)
                ? (_openBlock(), _createElementBlock("circle", {
                    key: 2,
                    cx: "100",
                    cy: "100",
                    r: "80",
                    fill: "transparent",
                    stroke: "#ffc107",
                    "stroke-width": "30",
                    "stroke-dasharray": getDashArray(counts.value.soon),
                    "stroke-dashoffset": getDashOffset(counts.value.expired + counts.value.immediately),
                    transform: "rotate(-90 100 100)",
                    class: "pie-segment",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (showTooltip($event, 'Expiring ≤ 30 days', counts.value.soon)))
                  }, [
                    _createElementVNode("title", null, _toDisplayString(getTooltip('Expiring ≤ 30 days', counts.value.soon)), 1)
                  ], 8, _hoisted_8))
                : _createCommentVNode("", true),
              _createElementVNode("text", _hoisted_9, _toDisplayString(__props.total), 1),
              _cache[6] || (_cache[6] = _createElementVNode("text", {
                x: "100",
                y: "115",
                "text-anchor": "middle",
                "font-size": "10",
                fill: "#666"
              }, " Total ", -1))
            ]))
          ]),
          _createElementVNode("div", _hoisted_10, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(legendItems.value, (item, key) => {
              return (_openBlock(), _createElementBlock("label", {
                class: "legend-item",
                key: key
              }, [
                _withDirectives(_createElementVNode("input", {
                  type: "checkbox",
                  "onUpdate:modelValue": ($event: any) => ((item.checked) = $event),
                  onChange: onFilterChange
                }, null, 40, _hoisted_11), [
                  [_vModelCheckbox, item.checked]
                ]),
                _createElementVNode("span", {
                  class: "legend-color",
                  style: _normalizeStyle({ background: item.color })
                }, null, 4),
                _createElementVNode("span", null, _toDisplayString(item.label) + " (" + _toDisplayString(item.count) + ")", 1)
              ]))
            }), 128))
          ])
        ]),
        (tooltipVisible.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "chart-tooltip",
              style: _normalizeStyle({ left: tooltipX.value + 'px', top: tooltipY.value + 'px' })
            }, _toDisplayString(tooltipText.value), 5))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_12, [
          _createElementVNode("table", _hoisted_13, [
            _cache[7] || (_cache[7] = _createElementVNode("thead", null, [
              _createElementVNode("tr", null, [
                _createElementVNode("th", null, "Name"),
                _createElementVNode("th", null, "Contact"),
                _createElementVNode("th", null, "ExpiryDate"),
                _createElementVNode("th", null, "Expiry Days"),
                _createElementVNode("th", null, "Doc No."),
                _createElementVNode("th", null, "Email")
              ])
            ], -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.data, (record) => {
              return (_openBlock(), _createElementBlock("tr", {
                key: record.documentExpiryResultId,
                class: _normalizeClass(getRowClass(record))
              }, [
                _createElementVNode("td", null, _toDisplayString(record.officerName), 1),
                _createElementVNode("td", null, _toDisplayString(record.contact), 1),
                _createElementVNode("td", null, _toDisplayString(_unref(formatDate)(record.expiryDate)), 1),
                _createElementVNode("td", null, _toDisplayString(record.daysUntilExpiry <= 0 ? 'Expired':record.daysUntilExpiry + ' days'), 1),
                _createElementVNode("td", null, _toDisplayString(record.documentNumber), 1),
                _createElementVNode("td", null, _toDisplayString(record.email), 1)
              ], 2))
            }), 128))
          ])
        ]),
        (__props.endPage > 1)
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _withDirectives(_createElementVNode("button", {
                class: "Btn btn-primary btn-sm m-1",
                onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('prev-page')))
              }, "<< ", 512), [
                [_vShow, __props.isRangeLess]
              ]),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.endPage, (pageNo) => {
                return (_openBlock(), _createElementBlock("span", {
                  key: 'page-' + pageNo
                }, [
                  (isShownPageButton(pageNo))
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: _normalizeClass(["btn btn-success btn-sm", {'btn-selected': (pageNo - 1) === __props.currentPage}]),
                        type: "button",
                        onClick: ($event: any) => (_ctx.$emit('page-change', pageNo - 1))
                      }, _toDisplayString(pageNo), 11, _hoisted_15))
                    : _createCommentVNode("", true)
                ]))
              }), 128)),
              _withDirectives(_createElementVNode("button", {
                type: "button",
                class: "Btn btn-primary btn-sm m-1",
                onClick: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('next-page')))
              }, " >> ", 512), [
                [_vShow, __props.isRangeGreater]
              ])
            ]))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})